import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  RefreshCw, Users, Circle, Clock, LogIn, LogOut, AlertTriangle, Loader2, Megaphone, X,
} from 'lucide-react';
import AdminLayout from './AdminLayout';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WINDOW_OPTIONS = [
  { value: 5, label: '5 min' },
  { value: 10, label: '10 min' },
  { value: 30, label: '30 min' },
  { value: 60, label: '1 hour' },
];

const EVENT_STYLES = {
  login_success: { icon: LogIn,       cls: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20', label: 'Login' },
  login_failure: { icon: AlertTriangle, cls: 'text-red-400 bg-red-500/10 border-red-500/20', label: 'Failed' },
  logout:        { icon: LogOut,      cls: 'text-slate-400 bg-white/5 border-white/10', label: 'Logout' },
};

const relTime = (isoStr) => {
  if (!isoStr) return '—';
  const diff = Math.max(0, (Date.now() - new Date(asUtc(isoStr)).getTime()) / 1000);
  if (diff < 60) return `${Math.round(diff)}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
};

const fmtDuration = (secs) => {
  if (secs == null) return '—';
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ${secs % 60}s`;
  return `${Math.floor(secs / 3600)}h ${Math.floor((secs % 3600) / 60)}m`;
};

// Brisbane wall-clock formatters. All backend timestamps land here as
// ISO-8601 UTC strings (e.g. `2026-08-24T03:02:08.123Z`); we render
// them in Australia/Brisbane so Darren + the Yabbit team see their
// local time without doing UTC→AEST maths in their head. Brisbane has
// no DST so the suffix stays AEST year-round.
const BRISBANE_TZ = 'Australia/Brisbane';
// Older backend rows were serialised without a UTC offset — treat any
// offset-less ISO string as UTC so it can't be misparsed as local time.
const asUtc = (isoStr) => (typeof isoStr === 'string' && !/([zZ]|[+-]\d{2}:?\d{2})$/.test(isoStr) ? `${isoStr}Z` : isoStr);
const fmtBrisbaneTime = (isoStr) => {
  if (!isoStr) return '—';
  try {
    const t = new Date(asUtc(isoStr)).toLocaleTimeString('en-AU', {
      hour12: false, timeZone: BRISBANE_TZ,
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    return `${t} AEST`;
  } catch { return '—'; }
};
const fmtBrisbaneDateTime = (isoStr) => {
  if (!isoStr) return '—';
  try {
    const d = new Date(asUtc(isoStr));
    // en-CA gives YYYY-MM-DD which sorts cleanly and matches the old layout
    const date = d.toLocaleDateString('en-CA', { timeZone: BRISBANE_TZ });
    const time = d.toLocaleTimeString('en-AU', {
      hour12: false, timeZone: BRISBANE_TZ,
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    return `${date} ${time} AEST`;
  } catch { return '—'; }
};

const DEFAULT_MESSAGE = 'A deployment is starting in the next few minutes — please save your work and expect a brief interruption.';
const DURATION_PRESETS = [
  { value: 5,  label: '5 min' },
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 60, label: '1 hour' },
];

// Modal: master admin composes a broadcast that every logged-in user
// will see as a sticky banner (see BroadcastBanner). Auto-expires so
// stale warnings never linger.
const NotifyUsersModal = ({ headers, activeCount, onClose }) => {
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [severity, setSeverity] = useState('warning');
  const [durationMinutes, setDurationMinutes] = useState(15);
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      await axios.post(
        `${API}/admin/notifications`,
        { message: message.trim(), severity, duration_minutes: durationMinutes },
        { headers },
      );
      toast.success(`Broadcast sent — ${activeCount} active user${activeCount === 1 ? '' : 's'} will see it within 30s.`);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send broadcast');
    } finally {
      setSending(false);
    }
  };

  const dismissAll = async () => {
    if (!window.confirm('Dismiss ALL active broadcasts for every user?')) return;
    setSending(true);
    try {
      const r = await axios.delete(`${API}/admin/notifications`, { headers });
      toast.success(`Dismissed ${r.data?.dismissed || 0} broadcast${r.data?.dismissed === 1 ? '' : 's'}.`);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to dismiss');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="notify-modal">
      <div className="bg-[#0D1526] border border-white/10 rounded-2xl w-full max-w-lg shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h2 className="text-white font-semibold flex items-center gap-2">
            <Megaphone className="w-4 h-4 text-amber-400" />
            Notify Active Users
          </h2>
          <button onClick={onClose} className="text-white/30 hover:text-white" data-testid="notify-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="text-xs text-white/50 block mb-1">Message</label>
            <textarea
              rows={3}
              value={message}
              onChange={e => setMessage(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-amber-500/50 resize-none"
              maxLength={500}
              data-testid="notify-message"
            />
            <p className="text-white/25 text-[10px] mt-1">{message.length}/500 · shown to every logged-in user</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-white/50 block mb-1">Severity</label>
              <select
                value={severity}
                onChange={e => setSeverity(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500/50"
                data-testid="notify-severity"
              >
                <option value="info">Info (blue)</option>
                <option value="warning">Warning (amber)</option>
                <option value="critical">Critical (red)</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-white/50 block mb-1">Show for</label>
              <select
                value={durationMinutes}
                onChange={e => setDurationMinutes(Number(e.target.value))}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500/50"
                data-testid="notify-duration"
              >
                {DURATION_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
          </div>
          <div className="rounded-lg bg-amber-500/5 border border-amber-500/20 px-3 py-2 text-amber-300 text-xs">
            {activeCount === 0
              ? 'No users are currently active. Broadcast will still be stored and shown to anyone who logs in during the window.'
              : `${activeCount} active user${activeCount === 1 ? '' : 's'} will see this within ~30 seconds (poll interval).`}
          </div>
        </div>
        <div className="flex items-center justify-between gap-2 px-6 py-4 border-t border-white/10">
          <button
            onClick={dismissAll}
            disabled={sending}
            className="text-white/40 hover:text-red-400 text-xs"
            data-testid="notify-dismiss-all-btn"
            title="Expire every active broadcast — use after deploy completes"
          >
            Dismiss all active
          </button>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 text-white/50 hover:text-white text-sm">Cancel</button>
            <button
              onClick={submit}
              disabled={sending || !message.trim()}
              className="flex items-center gap-2 px-5 py-2 bg-amber-500 hover:bg-amber-400 disabled:bg-white/10 disabled:text-white/30 text-slate-950 text-sm font-semibold rounded-lg"
              data-testid="notify-send-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Megaphone className="w-4 h-4" />}
              Send broadcast
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminActivityPage = () => {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [windowMinutes, setWindowMinutes] = useState(10);
  const [historyDays, setHistoryDays] = useState(7);
  const [activeUsers, setActiveUsers] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [loadingActive, setLoadingActive] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [filterEmail, setFilterEmail] = useState('');
  const [notifyOpen, setNotifyOpen] = useState(false);

  const loadActive = useCallback(async () => {
    setLoadingActive(true);
    try {
      // Two parallel windows: "right now" and "last 24h" so master admin
      // sees WHO has been on the platform recently (not just this moment).
      // Session persistence via httpOnly cookie means users often skip
      // /auth/login entirely on revisit — `last_activity_at` on users is
      // the source of truth for real presence.
      const [nowR, recentR] = await Promise.all([
        axios.get(`${API}/admin/activity/active-users?minutes=${windowMinutes}`, { headers }),
        axios.get(`${API}/admin/activity/active-users?minutes=1440`, { headers }),
      ]);
      setActiveUsers(nowR.data?.users || []);
      setRecentUsers(recentR.data?.users || []);
    } catch (err) {
      toast.error('Failed to load active users');
      console.error('active-users error', err);
    } finally {
      setLoadingActive(false);
    }
  }, [windowMinutes, headers]);

  const loadEvents = useCallback(async () => {
    setLoadingEvents(true);
    try {
      const params = new URLSearchParams({ days: String(historyDays), limit: '200' });
      if (filterEmail.trim()) params.set('email', filterEmail.trim());
      const r = await axios.get(`${API}/admin/activity/login-events?${params}`, { headers });
      setEvents(r.data?.events || []);
    } catch (err) {
      toast.error('Failed to load login events');
      console.error('login-events error', err);
    } finally {
      setLoadingEvents(false);
    }
  }, [historyDays, filterEmail, headers]);

  useEffect(() => { loadActive(); }, [loadActive]);
  useEffect(() => { loadEvents(); }, [loadEvents]);

  // Auto-refresh active users every 15s so the master admin can see who
  // shows up as they log in — no need to hammer F5 before a deploy.
  useEffect(() => {
    const id = setInterval(loadActive, 15000);
    return () => clearInterval(id);
  }, [loadActive]);

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-activity-page">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-white">User Activity</h1>
            <p className="text-white/40 text-sm mt-0.5">
              Who is on the platform right now, who has been on in the last 24 hours,
              and the explicit login/logout audit trail. Auto-refreshes every 15 seconds.
            </p>
          </div>
          <button
            onClick={() => setNotifyOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-slate-950 text-sm font-semibold rounded-lg transition-colors"
            data-testid="notify-active-users-btn"
          >
            <Megaphone className="w-4 h-4" />
            Notify Active Users
          </button>
        </div>

        {notifyOpen && (
          <NotifyUsersModal
            headers={headers}
            activeCount={activeUsers.length}
            onClose={() => setNotifyOpen(false)}
          />
        )}

        {/* Active Now panel */}
        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-white/10 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-emerald-500/15 rounded-lg flex items-center justify-center">
                <Users className="w-4 h-4 text-emerald-400" />
              </div>
              <div>
                <h2 className="text-white font-semibold text-sm flex items-center gap-2">
                  <span data-testid="active-users-count">{activeUsers.length}</span> active
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                </h2>
                <p className="text-white/40 text-xs">Users with API activity in the last {windowMinutes} minutes</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-white/40 text-xs">Window:</label>
              <select
                value={windowMinutes}
                onChange={e => setWindowMinutes(Number(e.target.value))}
                className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs focus:outline-none focus:border-teal-500/50"
                data-testid="active-window-select"
              >
                {WINDOW_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <button
                onClick={loadActive}
                disabled={loadingActive}
                className="flex items-center gap-1.5 text-white/50 hover:text-white text-xs px-2 py-1.5 border border-white/10 rounded-lg hover:bg-white/5"
                data-testid="refresh-active-btn"
              >
                {loadingActive ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                Refresh
              </button>
            </div>
          </div>
          {activeUsers.length === 0 ? (
            <div className="px-5 py-6 text-center text-white/40 text-sm" data-testid="active-users-empty">
              <p>No API activity in the last {windowMinutes} minutes.</p>
              {recentUsers.length > 0 ? (
                <p className="text-amber-300/80 text-xs mt-2">
                  ⚠ <span className="font-semibold">{recentUsers.length} user{recentUsers.length === 1 ? ' was' : 's were'}</span> on the platform in the last 24 hours — they may return at any moment. Check the &ldquo;Recent 24h&rdquo; panel below before deploying.
                </p>
              ) : (
                <p className="text-emerald-300/70 text-xs mt-2">Nobody has touched the platform in the last 24h — safe to deploy.</p>
              )}
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {activeUsers.map(u => (
                <div key={u.id} className="flex items-center gap-3 px-5 py-3" data-testid={`active-user-${u.id}`}>
                  <Circle className="w-2 h-2 text-emerald-400 fill-emerald-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm truncate">
                      {u.name || u.email}
                      <span className="text-white/30 text-xs ml-2">{u.email}</span>
                    </p>
                    <p className="text-white/40 text-xs">
                      {u.role}{u.domain_id ? ` · domain ${u.domain_id.slice(0, 8)}…` : ' · master'}
                    </p>
                  </div>
                  <div className="text-right text-xs">
                    <p className="text-emerald-300">Active {relTime(u.last_activity_at)}</p>
                    <p className="text-white/25">{fmtBrisbaneTime(u.last_activity_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent 24h presence — belt-and-braces for pre-deploy checks.
            Because users can maintain a session for hours via the httpOnly
            cookie without ever hitting /auth/login again, this widened
            window is the truest signal of "who's been on the platform
            recently and might be back before your deploy finishes". */}
        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden" data-testid="recent-24h-panel">
          <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-white/10 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-amber-500/15 rounded-lg flex items-center justify-center">
                <Clock className="w-4 h-4 text-amber-400" />
              </div>
              <div>
                <h2 className="text-white font-semibold text-sm">
                  <span data-testid="recent-users-count">{recentUsers.length}</span> on platform in last 24 hours
                </h2>
                <p className="text-white/40 text-xs">
                  Users whose session made any API call in the last 24h — includes silent revisits via saved cookie (not just explicit logins).
                </p>
              </div>
            </div>
          </div>
          {recentUsers.length === 0 ? (
            <div className="px-5 py-6 text-center text-white/30 text-xs" data-testid="recent-users-empty">
              Nobody has touched the platform in the last 24 hours.
            </div>
          ) : (
            <div className="divide-y divide-white/5 max-h-80 overflow-y-auto">
              {recentUsers.map(u => {
                const isActiveNow = activeUsers.some(a => a.id === u.id);
                return (
                  <div key={u.id} className="flex items-center gap-3 px-5 py-2.5" data-testid={`recent-user-${u.id}`}>
                    <Circle className={`w-2 h-2 flex-shrink-0 ${isActiveNow
                      ? 'text-emerald-400 fill-emerald-400'
                      : 'text-amber-400/60 fill-amber-400/60'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-white/80 text-sm truncate">
                        {u.name || u.email}
                        <span className="text-white/30 text-xs ml-2">{u.email}</span>
                      </p>
                      <p className="text-white/40 text-[11px]">
                        {u.role}{u.domain_id ? ` · domain ${u.domain_id.slice(0, 8)}…` : ' · master'}
                      </p>
                    </div>
                    <div className="text-right text-xs whitespace-nowrap">
                      <p className={isActiveNow ? 'text-emerald-300' : 'text-amber-300/80'}>
                        {relTime(u.last_activity_at)}
                      </p>
                      <p className="text-white/25 text-[10px]">{fmtBrisbaneTime(u.last_activity_at)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Login Events history */}
        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-white/10 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-teal-500/15 rounded-lg flex items-center justify-center">
                <Clock className="w-4 h-4 text-teal-400" />
              </div>
              <div>
                <h2 className="text-white font-semibold text-sm">Login history</h2>
                <p className="text-white/40 text-xs">
                  Explicit <code className="text-white/50 bg-white/10 rounded px-1">/auth/login</code> and <code className="text-white/50 bg-white/10 rounded px-1">/auth/logout</code> events — last {historyDays} days · retained 90 days · {events.length} events
                </p>
                <p className="text-amber-300/60 text-[10px] mt-0.5">
                  Users who resume via saved cookie won&apos;t appear here. See &ldquo;Recent 24h&rdquo; above for true presence.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <input
                type="text"
                value={filterEmail}
                onChange={e => setFilterEmail(e.target.value)}
                placeholder="Filter by email…"
                className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs placeholder-white/25 focus:outline-none focus:border-teal-500/50 w-56"
                data-testid="events-email-filter"
              />
              <select
                value={historyDays}
                onChange={e => setHistoryDays(Number(e.target.value))}
                className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs focus:outline-none focus:border-teal-500/50"
                data-testid="events-days-select"
              >
                <option value={1}>1 day</option>
                <option value={7}>7 days</option>
                <option value={30}>30 days</option>
                <option value={90}>90 days</option>
              </select>
              <button
                onClick={loadEvents}
                disabled={loadingEvents}
                className="flex items-center gap-1.5 text-white/50 hover:text-white text-xs px-2 py-1.5 border border-white/10 rounded-lg hover:bg-white/5"
                data-testid="refresh-events-btn"
              >
                {loadingEvents ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                Refresh
              </button>
              <button
                onClick={async () => {
                  try {
                    const r = await axios.get(`${API}/admin/activity/login-events.csv`, {
                      headers, params: { days: historyDays }, responseType: 'blob',
                    });
                    const url = URL.createObjectURL(r.data);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `login-audit-last${historyDays}d.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                  } catch { /* silent — button stays usable */ }
                }}
                className="flex items-center gap-1.5 text-teal-300/80 hover:text-teal-200 text-xs px-2 py-1.5 border border-teal-500/30 rounded-lg hover:bg-teal-500/10"
                data-testid="export-events-csv-btn"
              >
                Export CSV
              </button>
            </div>
          </div>
          {events.length === 0 ? (
            <div className="px-5 py-10 text-center text-white/30 text-sm" data-testid="events-empty">
              No login events in this range.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-white/40 text-[11px] uppercase tracking-wider">
                    <th className="px-5 py-2 font-medium">When</th>
                    <th className="px-3 py-2 font-medium">Event</th>
                    <th className="px-3 py-2 font-medium">User</th>
                    <th className="px-3 py-2 font-medium">Role</th>
                    <th className="px-3 py-2 font-medium">IP</th>
                    <th className="px-3 py-2 font-medium">Session</th>
                    <th className="px-5 py-2 font-medium">User-Agent</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {events.map(ev => {
                    const style = EVENT_STYLES[ev.event] || EVENT_STYLES.logout;
                    const EvIcon = style.icon;
                    return (
                      <tr key={ev.id} className="hover:bg-white/[0.02]" data-testid={`event-row-${ev.id}`}>
                        <td className="px-5 py-2.5 text-white/60 whitespace-nowrap">
                          <div>{relTime(ev.timestamp)}</div>
                          <div className="text-white/25 text-[10px]">{fmtBrisbaneDateTime(ev.timestamp)}</div>
                        </td>
                        <td className="px-3 py-2.5">
                          <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border ${style.cls}`}>
                            <EvIcon className="w-3 h-3" />{style.label}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-white/70">
                          <div>{ev.name || <span className="text-white/30">—</span>}</div>
                          <div className="text-white/30 text-[10px]">{ev.email}</div>
                        </td>
                        <td className="px-3 py-2.5 text-white/50">{ev.role || <span className="text-white/25">—</span>}</td>
                        <td className="px-3 py-2.5 text-white/50 font-mono text-[11px]">{ev.ip || '—'}</td>
                        <td className="px-3 py-2.5 text-white/50">{fmtDuration(ev.session_duration_seconds)}</td>
                        <td className="px-5 py-2.5 text-white/30 text-[10px] max-w-xs truncate" title={ev.user_agent}>{ev.user_agent || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminActivityPage;
